import { Mark, mergeAttributes, Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { ySyncPluginKey } from 'y-prosemirror';
import { LIVEBLOCKS_COMMENT_MARK_TYPE, THREADS_PLUGIN_KEY, ThreadPluginActions, THREADS_ACTIVE_SELECTION_PLUGIN } from '../types.js';

const Comment = Mark.create({
  name: LIVEBLOCKS_COMMENT_MARK_TYPE,
  excludes: "",
  inclusive: false,
  keepOnSplit: true,
  parseHTML: () => {
    return [
      {
        tag: "span",
        getAttrs: (node) => node.getAttribute("data-lb-thread-id") !== null && null
      }
    ];
  },
  addAttributes() {
    return {
      orphan: {
        parseHTML: (element) => !!element.getAttribute("data-orphan"),
        renderHTML: (attributes) => {
          return attributes.orphan ? {
            "data-orphan": "true"
          } : {};
        },
        default: false
      },
      threadId: {
        parseHTML: (element) => element.getAttribute("data-lb-thread-id"),
        renderHTML: (attributes) => {
          return {
            "data-lb-thread-id": attributes.threadId
          };
        },
        default: ""
      }
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: "lb-root lb-tiptap-thread-mark"
      })
    ];
  },
  addProseMirrorPlugins() {
    const updateState = (doc, selectedThreadId) => {
      const threadPositions = /* @__PURE__ */ new Map();
      const decorations = [];
      doc.descendants((node, pos) => {
        node.marks.forEach((mark) => {
          if (mark.type === this.type) {
            const thisThreadId = mark.attrs.threadId;
            if (!thisThreadId) {
              return;
            }
            const from = pos;
            const to = from + node.nodeSize;
            const currentPosition = threadPositions.get(thisThreadId) ?? {
              from: Infinity,
              to: 0
            };
            threadPositions.set(thisThreadId, {
              from: Math.min(from, currentPosition.from),
              to: Math.max(to, currentPosition.to)
            });
            if (selectedThreadId === thisThreadId) {
              decorations.push(
                Decoration.inline(from, to, {
                  class: "lb-root lb-tiptap-thread-mark-selected"
                })
              );
            }
          }
        });
      });
      return {
        decorations: DecorationSet.create(doc, decorations),
        selectedThreadId,
        threadPositions,
        selectedThreadPos: selectedThreadId !== null ? threadPositions.get(selectedThreadId)?.to ?? null : null
      };
    };
    return [
      new Plugin({
        key: THREADS_PLUGIN_KEY,
        state: {
          init() {
            return {
              threadPositions: /* @__PURE__ */ new Map(),
              selectedThreadId: null,
              selectedThreadPos: null,
              decorations: DecorationSet.empty
            };
          },
          apply(tr, state) {
            const action = tr.getMeta(THREADS_PLUGIN_KEY);
            if (!tr.docChanged && !action) {
              return state;
            }
            if (!action) {
              return updateState(tr.doc, state.selectedThreadId);
            }
            if (action.name === ThreadPluginActions.SET_SELECTED_THREAD_ID && state.selectedThreadId !== action.data) {
              return updateState(tr.doc, action.data);
            }
            return state;
          }
        },
        props: {
          decorations: (state) => {
            return THREADS_PLUGIN_KEY.getState(state)?.decorations ?? DecorationSet.empty;
          },
          handleClick: (view, pos, event) => {
            if (event.button !== 0) {
              return;
            }
            const selectThread = (threadId2) => {
              view.dispatch(
                view.state.tr.setMeta(THREADS_PLUGIN_KEY, {
                  name: ThreadPluginActions.SET_SELECTED_THREAD_ID,
                  data: threadId2
                })
              );
            };
            const node = view.state.doc.nodeAt(pos);
            if (!node) {
              selectThread(null);
              return;
            }
            const commentMark = node.marks.find(
              (mark) => mark.type === this.type
            );
            if (commentMark?.attrs.orphan) {
              selectThread(null);
              return;
            }
            const threadId = commentMark?.attrs.threadId;
            selectThread(threadId ?? null);
          }
        }
      })
    ];
  }
});
const CommentsExtension = Extension.create({
  name: "liveblocksComments",
  priority: 95,
  addExtensions() {
    return [Comment];
  },
  addStorage() {
    return {
      pendingComment: false
    };
  },
  addCommands() {
    return {
      addPendingComment: () => () => {
        if (this.editor.state.selection.empty) {
          return false;
        }
        this.editor.view.dispatch(
          this.editor.state.tr.setMeta(THREADS_PLUGIN_KEY, {
            name: ThreadPluginActions.SET_SELECTED_THREAD_ID,
            data: null
          })
        );
        this.storage.pendingComment = true;
        return true;
      },
      closePendingComment: () => () => {
        this.storage.pendingComment = false;
        return true;
      },
      selectThread: (id) => () => {
        this.editor.view.dispatch(
          this.editor.state.tr.setMeta(THREADS_PLUGIN_KEY, {
            name: ThreadPluginActions.SET_SELECTED_THREAD_ID,
            data: id
          })
        );
        return true;
      },
      addComment: (id) => ({ commands }) => {
        if (!this.storage.pendingComment || this.editor.state.selection.empty) {
          return false;
        }
        commands.setMark(LIVEBLOCKS_COMMENT_MARK_TYPE, { threadId: id });
        this.storage.pendingComment = false;
        return true;
      }
    };
  },
  onSelectionUpdate({ transaction }) {
    if (!this.storage.pendingComment || transaction.getMeta(ySyncPluginKey)) {
      return;
    }
    this.storage.pendingComment = false;
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: THREADS_ACTIVE_SELECTION_PLUGIN,
        props: {
          decorations: ({ doc, selection }) => {
            if (!this.storage.pendingComment) {
              return DecorationSet.create(doc, []);
            }
            const { from, to } = selection;
            const decorations = [
              Decoration.inline(from, to, {
                class: "lb-root lb-selection lb-tiptap-active-selection"
              })
            ];
            return DecorationSet.create(doc, decorations);
          }
        }
      })
    ];
  }
});

export { CommentsExtension };
//# sourceMappingURL=CommentsExtension.js.map
